import { useMemo } from 'react'
import { computeChainSelectionSubRanges } from '@/lib/text-linking/locate-chain-caret-destination'
import { buildActiveIsolationAncestorDepthMap } from '@/lib/groups/group-isolation-opacity'
import { orderShapesWithGroupShellsBeforeDirectChildren } from '@/lib/groups/shape-group-paint-order'
import { dedupeShapesByIdPreserveOrder } from '../helpers/shape-list'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { EditScopeRefsApi } from './use-edit-scope-refs'
import type { SelectionPresentationApi } from './use-selection-presentation'

export function useShapeRenderModel(
  ctx: ShapeManagerStateApi & EditScopeRefsApi & SelectionPresentationApi
) {
  const {
    chainFrameIdsByShapeIdRef,
    chainTextSelection,
    editScopeGroupId,
    getLinkedTextChainsRef,
    linkedTextChains,
    multiSelectChrome,
    multiSelectChromeRef,
    selectedShapeIds,
    shapeBounds,
    shapeBoundsRef,
    shapes,
    shapesById,
  } = ctx

  const selectedIdSet = useMemo(() => new Set(selectedShapeIds), [selectedShapeIds])

  const shapesForPaintOrder = useMemo(
    () =>
      orderShapesWithGroupShellsBeforeDirectChildren(
        dedupeShapesByIdPreserveOrder(shapes.filter((shape) => shape.hidden !== true))
      ),
    [shapes]
  )

  // Keep in sync for imperative reads (e.g. object panel) in the same commit as paint — effects
  // can run before sibling chrome effects and would otherwise see a stale ref for one frame.
  multiSelectChromeRef.current = multiSelectChrome
  shapeBoundsRef.current = shapeBounds
  // The prop, not `editScopeGroupIdRef`: that ref is synced in an effect, so the render that enters
  // or leaves isolation would dim (or undim) against the previous scope, and the memoized layer
  // items get no later render to correct it.
  const activeGroupIsolationId = editScopeGroupId
  const activeIsolationAncestorDepthById =
    activeGroupIsolationId != null
      ? buildActiveIsolationAncestorDepthMap(activeGroupIsolationId, shapesById)
      : null

  // Chain frame ids in flow order, keyed by every member's shape id (WMPCM-9199). Built once per
  // render rather than per shape: `chainContaining` is a linear scan of every chain, and the
  // result is shared by each member of the same chain anyway.
  const chainFrameIdsByShapeId = new Map<string, string[]>()
  // Prefer the prop — it is the only one of the two that re-renders this component when the chains
  // change (WMPCM-6867). The getter stays as the fallback so a caller that wires only it keeps
  // working. Derived ONCE per render and shared by every render-time chain read below
  // (`hasNextInChain`/`hasPrevInChain`/`onSelectChain`), which each used to re-invoke the getter
  // per shape — a linear scan of every chain, per frame, per render.
  const liveChains = linkedTextChains ?? getLinkedTextChainsRef.current?.() ?? []

  if (liveChains.length > 0) {
    const shapeIdByUid = new Map<string, string>()

    for (const s of shapesForPaintOrder) {
      if (s.uid != null) shapeIdByUid.set(String(s.uid), s.id)
    }

    for (const chain of liveChains) {
      const ids = chain.textBox
        .map((tb) => shapeIdByUid.get(tb.uid))
        .filter((id): id is string => id != null)

      if (ids.length > 1) {
        for (const id of ids) chainFrameIdsByShapeId.set(id, ids)
      }
    }
  }

  chainFrameIdsByShapeIdRef.current = chainFrameIdsByShapeId

  // Each member's slice of the live cross-frame selection, in that member's local offsets.
  const chainSelectionRangeByShapeId = new Map<string, { start: number; end: number }>()

  if (chainTextSelection) {
    const subRanges = computeChainSelectionSubRanges(
      chainTextSelection.frameLengths,
      chainTextSelection.anchorGlobal,
      chainTextSelection.focusGlobal
    )

    for (const sub of subRanges) {
      const id = chainTextSelection.frameIds[sub.frameIndex]

      if (id) chainSelectionRangeByShapeId.set(id, { start: sub.start, end: sub.end })
    }
  }

  return {
    selectedIdSet,
    shapesForPaintOrder,
    activeGroupIsolationId,
    activeIsolationAncestorDepthById,
    chainFrameIdsByShapeId,
    liveChains,
    chainSelectionRangeByShapeId,
  }
}

export type ShapeRenderModelApi = ReturnType<typeof useShapeRenderModel>
